import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useWebsite } from "../shared/hooks/useWebsite";
import { useWebsiteBuilderLoadingState } from "../shared/hooks/useWebsiteBuilderLoadingState";
import { IWebsiteBuilderFormSummary } from "redux/reducers/models/websiteBuilder";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxArchive, faMinusCircle, faPalette } from "@fortawesome/pro-regular-svg-icons";
import { useHistory } from "react-router";
import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { GetWebsiteBuilderFormSummaries } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import DataTable from "components/dataTable/DataTable";
import moment from "moment";
import { ButtonNew as Button } from "components/buttonNew";
import { cloneDeep } from "lodash";
import Input from "components/form/input/Input";
import useDebounce from "hooks/events/useDebounce";
import "./websiteBuilderFormSummaries.scss";

const FORM_SUMMARIES_LIMIT = 25;

export function WebsiteBuilderFormSummaries() {
  const history = useHistory();

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { website, websiteLoadingStatus } = useWebsite({ websiteMustExist: true });

  const windowSize = useWindowSize();

  const noDataCardRef = useRef<HTMLDivElement>(null);
  const noDataCardDebounceRef = useRef<NodeJS.Timeout>(null);
  
  const [noDataCardPosition, setNoDataCardPosition] = useState<number>(0);
  
  const noDataCardHeight = windowSize.height - noDataCardPosition - 1;
  
  const {
    state: initialFormSummaries,
    stateExists: initialFormSummariesExists,
    stateLoadingStatus: initialFormSummariesLoadingStatus,
    loadInProgress: initialFormSummariesLoadInProgress,
    loadFailed: initialFormSummariesLoadFailed,
    loadSucceeded: initialFormSummariesLoadSucceeded,
  } = useWebsiteBuilderLoadingState<IWebsiteBuilderFormSummary[]>({
    state: [],
    stateExists: false,
    stateLoadingStatus: "loading",
  });

  const noDataCardHeightActive = initialFormSummariesExists && initialFormSummaries?.length === 0 && windowSize.height > 600;

  useLayoutEffect(() => {
    if (
      websiteLoadingStatus !== "succeeded" ||
      initialFormSummariesLoadingStatus !== "succeeded" ||
      !initialFormSummariesExists ||
      initialFormSummaries?.length > 0 ||
      !noDataCardRef.current
    ) {
      return;
    }

    const update = () => setNoDataCardPosition(noDataCardRef.current.getBoundingClientRect().y);

    update();

    const debouncedUpdate = () => {
      clearTimeout(noDataCardDebounceRef.current);

      noDataCardDebounceRef.current = setTimeout(update, 100);
    };

    const resizeObserver = new ResizeObserver(debouncedUpdate);

    resizeObserver.observe(noDataCardRef.current);

    return () => {
      clearTimeout(noDataCardDebounceRef.current);
      resizeObserver.disconnect();
    };
  }, [websiteLoadingStatus, initialFormSummariesLoadingStatus, initialFormSummariesExists, initialFormSummaries?.length]);

  const loadFormSummaries = useCallback(
    async (token: CancelToken) => {
      if (website?.id != null) {
        initialFormSummariesLoadInProgress();

        const formSummariesRes = await GetWebsiteBuilderFormSummaries(
          {
            website_id: website.id,
            offset: 0,
            limit: FORM_SUMMARIES_LIMIT,
          },
          false,
          token,
        );

        if (formSummariesRes.status !== StatusCode.OK) {
          if (!token.reason) {
            initialFormSummariesLoadFailed();
            dispatch(
              showError(typeof formSummariesRes.data === "string" ? formSummariesRes.data : t("Error loading forms")),
            );
          }

          return;
        }

        initialFormSummariesLoadSucceeded(formSummariesRes.data ?? [], true);
      }
    },
    [website?.id],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadFormSummaries(source.token);

    return () => source.cancel();
  }, [loadFormSummaries]);

  function navigateToEditor() {
    history.push("/admin/website-builder-editor");
  }

  function renderFormSummaries() {
    if (
      websiteLoadingStatus === "succeeded" &&
      initialFormSummariesLoadingStatus === "succeeded" &&
      initialFormSummariesExists
    ) {
      if (initialFormSummaries.length === 0) {
        return (
          <div
            ref={noDataCardRef}
            className="form-summaries-no-data-card"
            style={{
              height: noDataCardHeightActive ? noDataCardHeight : null,
            }}
          >
            <div className="no-data">
              <div className="no-data-icon">
                <FontAwesomeIcon icon={faMinusCircle} />
              </div>
              <div className="no-data-message">
                <div className="no-data-message-primary">{t("No data found")}</div>
                <div className="no-data-message-secondary">
                  {t("Add a form to your website to receive submissions and view its details here.")}
                </div>
              </div>
              <Button
                type="primary"
                onClick={navigateToEditor}
              >
                {t("Add forms in the editor")}
              </Button>
            </div>
          </div>
        );
      } else {
        return <FormSummariesTable websiteId={website?.id} initialFormSummaries={initialFormSummaries} />;
      }
    } else {
      return null;
    }
  }

  return (
    <Page
      id="website-builder-form-summaries-page"
      className={noDataCardHeightActive ? "form-summaries-page-no-padding" : null}
      narrow
      title={t("Forms & submissions")}
      subtitle={t("View all forms published on your website and their submission counts.")}
      primaryAction={{
        content: (
          <>
            <FontAwesomeIcon icon={faPalette} className="mr-2" />
            {t("Customize website")}
          </>
        ),
        action: navigateToEditor,
        disabled: websiteLoadingStatus !== "succeeded",
      }}
    >
      <WebsiteBuilderLoadingContent
        loading={
          websiteLoadingStatus === "standby" ||
          websiteLoadingStatus === "loading" ||
          initialFormSummariesLoadingStatus === "loading"
        }
      >
        {renderFormSummaries()}
      </WebsiteBuilderLoadingContent>
    </Page>
  );
}

interface IFormSummariesTableProps {
  websiteId: number;
  initialFormSummaries: IWebsiteBuilderFormSummary[];
}

function FormSummariesTable(props: IFormSummariesTableProps) {
  const { websiteId, initialFormSummaries } = props;

  const history = useHistory();
  
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const paginationTriggered = useRef<boolean>(false);

  const [formSummaries, setFormSummaries] = useState<IWebsiteBuilderFormSummary[]>(cloneDeep(initialFormSummaries));
  const [formSummariesLoading, setFormSummariesLoading] = useState<boolean>(false);
  const [formSummariesOffset, setFormSummariesOffset] = useState<number>(0);

  const [searchedFormSummaries, setSearchedFormSummaries] = useState<IWebsiteBuilderFormSummary[]>([]);
  const [searchedFormSummariesLoading, setSearchedFormSummariesLoading] = useState<boolean>(false);
  const [searchedFormSummariesQuery, setSearchedFormSummariesQuery] = useState<string>("");

  const debouncedSearchedFormSummariesQuery = useDebounce(searchedFormSummariesQuery, 500);

  const searchedFormSummariesActive = debouncedSearchedFormSummariesQuery.length > 0;
  const activeFormSummaries = searchedFormSummariesActive ? searchedFormSummaries : formSummaries;

  const loadFormSummaries = useCallback(
    async (token: CancelToken) => {
      setFormSummariesLoading(true);

      const res = await GetWebsiteBuilderFormSummaries(
        {
          website_id: websiteId,
          offset: formSummariesOffset,
          limit: FORM_SUMMARIES_LIMIT,
        },
        false,
        token,
      );

      setFormSummariesLoading(false);

      if (res.status !== StatusCode.OK) {
        if (!token.reason) {
          dispatch(
            showError(typeof res.data === "string" ? res.data : t("Error loading forms")),
          );
        }

        setFormSummaries([]);
      } else {
        setFormSummaries(res.data ?? []);
      }
    },
    [websiteId, formSummariesOffset],
  );

  const loadSearchedFormSummaries = useCallback(
    async (search: string, token: CancelToken) => {
      setSearchedFormSummariesLoading(true);

      const res = await GetWebsiteBuilderFormSummaries(
        {
          website_id: websiteId,
          search,
        },
        false,
        token,
      );

      setSearchedFormSummariesLoading(false);

      if (res.status !== StatusCode.OK) {
        if (!token.reason) {
          dispatch(
            showError(typeof res.data === "string" ? res.data : t("Error loading forms")),
          );
        }

        setSearchedFormSummaries([]);
      } else {
        setSearchedFormSummaries(res.data ?? []);
      }
    },
    [websiteId],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (paginationTriggered.current && !searchedFormSummariesActive) {
      void loadFormSummaries(source.token);
    }

    return () => source.cancel();
  }, [searchedFormSummariesActive, loadFormSummaries]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (searchedFormSummariesActive) {
      void loadSearchedFormSummaries(debouncedSearchedFormSummariesQuery, source.token);
    } else {
      setSearchedFormSummaries([]);
      setSearchedFormSummariesLoading(false);
    }

    return () => source.cancel();
  }, [searchedFormSummariesActive, debouncedSearchedFormSummariesQuery, loadSearchedFormSummaries]);

  function navigateToFormSubmissions(id: number) {
    history.push(`/admin/website-builder-forms/form/${id}`);
  }

  return (
    <>
      <Input
        className="form-summaries-search"
        value={searchedFormSummariesQuery}
        onChange={e => setSearchedFormSummariesQuery(e.target.value)}
        placeholder={t("Search forms...")}
      />

      <DataTable
        columns={[
          { label: t("Form name") },
          { label: t("Last modified") },
          { label: t("Submissions") },
          { label: " " },
        ]}
        loading={searchedFormSummariesActive ? searchedFormSummariesLoading : formSummariesLoading}
        footer={{
          tableLimit: FORM_SUMMARIES_LIMIT,
          tableOffset: searchedFormSummariesActive ? 0 : formSummariesOffset,
          handleTableOffset: direction => {
            if (searchedFormSummariesActive) {
              return;
            }

            paginationTriggered.current = true;

            setFormSummariesOffset(prev =>
              direction === "prev"
                ? Math.max(0, prev - FORM_SUMMARIES_LIMIT)
                : prev + FORM_SUMMARIES_LIMIT,
            );
          },
          disableNextOffset: searchedFormSummariesActive || formSummaries.length < FORM_SUMMARIES_LIMIT,
        }}
      >
        {activeFormSummaries.map(summary => {
          return (
            <tr key={summary.id}>
              <td>
                <div className="form-summaries-name-container">
                  <div>{summary.name}</div>
                  {summary.archived_at != null && (
                    <FontAwesomeIcon
                      className="form-summaries-archived-icon"
                      icon={faBoxArchive}
                    />
                  )}
                </div>
              </td>
              <td>{summary.updated_at != null ? moment(summary.updated_at).format("MMM D, YYYY") : ""}</td>
              <td>{summary.submissions_count}</td>
              <td>
                <button
                  className="form-summaries-view-submissions"
                  onClick={() => navigateToFormSubmissions(summary.id)}
                >
                  {t("View submissions")}
                </button>
              </td>
            </tr>
          );
        })}
      </DataTable>
    </>
  );
}